import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { canViewAnalytics } from '../permissions';
import PowerBIReport from '../components/PowerBIReport';
import { parseWallClock } from '../utils/datetime';
import '../styles/home.css';
import '../styles/analytics.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function formatCurrency(val) {
  if (!val || val === 0) return '$0';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function pct(num, den) {
  if (!den) return null;
  return Math.round((num / den) * 100);
}

function isMyProject(project, displayName) {
  if (!displayName) return true;
  return project.Estimator === displayName
    || project.BidCoordinator === displayName
    || project.ProjectExecutive === displayName
    || project.ProjectManager === displayName;
}

// DateBidSubmitted is wall-clock; CreatedAt is a server UTC timestamp.
// Bucket each in its own zone so projects without DateBidSubmitted still
// land in the correct quarter relative to the user's local time.
function quarterKey(p) {
  let d = null;
  if (p.DateBidSubmitted) d = parseWallClock(p.DateBidSubmitted);
  else if (p.CreatedAt) d = new Date(p.CreatedAt);
  if (!d || isNaN(d)) return null;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()} Q${q}`;
}

// Group projects by a key extractor, then compute win/hit rates per group.
// `bidDate` chooses which date to bucket by for time-based grouping.
function buildBreakdown(projects, keyFn) {
  const map = {};
  projects.forEach(p => {
    const key = keyFn(p);
    if (!key) return;
    if (!map[key]) map[key] = { key, total: 0, awarded: 0, notAwarded: 0, awardedValue: 0, totalValue: 0 };
    map[key].total += 1;
    map[key].totalValue += p.ProjectCost || 0;
    if (p.StatusName === 'Awarded') {
      map[key].awarded += 1;
      map[key].awardedValue += p.ProjectCost || 0;
    }
    if (p.StatusName === 'Not Awarded') {
      map[key].notAwarded += 1;
    }
  });
  return Object.values(map).map(r => ({
    ...r,
    decided: r.awarded + r.notAwarded,
    winRate: pct(r.awarded, r.awarded + r.notAwarded),
    hitRate: pct(r.awarded, r.total),
  }));
}

export default function Analytics() {
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('company');
  const [minBids, setMinBids] = useState(3);
  // Reporting config comes from the server (admin-set default in Settings).
  // `activeMode` is the current view; users can flip between standard/advanced
  // via the switcher if advanced is configured.
  const [reportingConfig, setReportingConfig] = useState({ mode: 'standard' });
  const [activeMode, setActiveMode] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const [data, cfgRes] = await Promise.all([
          api.getProjects(),
          fetch(`${API_BASE}/public-settings/reporting`).then(r => r.ok ? r.json() : { mode: 'standard' }).catch(() => ({ mode: 'standard' })),
        ]);
        setAllProjects(data);
        setReportingConfig(cfgRes);
        setActiveMode(cfgRes.mode || 'standard');
      } catch (err) {
        console.error(err);
        setActiveMode('standard');
      }
      setLoading(false);
    }
    load();
  }, []);

  const projects = useMemo(() => {
    if (scope === 'company' || !user?.displayName) return allProjects;
    return allProjects.filter(p => isMyProject(p, user.displayName));
  }, [allProjects, scope, user]);

  const stats = useMemo(() => {
    const total = projects.length;
    const awarded = projects.filter(p => p.StatusName === 'Awarded');
    const notAwarded = projects.filter(p => p.StatusName === 'Not Awarded');
    const decided = awarded.length + notAwarded.length;
    const open = total - decided;

    const totalValue = projects.reduce((s, p) => s + (p.ProjectCost || 0), 0);
    const awardedValue = awarded.reduce((s, p) => s + (p.ProjectCost || 0), 0);
    const notAwardedValue = notAwarded.reduce((s, p) => s + (p.ProjectCost || 0), 0);
    const decidedValue = awardedValue + notAwardedValue;

    const winRate = pct(awarded.length, decided) ?? 0;
    const hitRate = pct(awarded.length, total) ?? 0;
    const dollarWinRate = pct(awardedValue, decidedValue) ?? 0;
    const dollarHitRate = pct(awardedValue, totalValue) ?? 0;

    const byEstimator = buildBreakdown(projects, p => p.Estimator)
      .filter(r => r.total >= minBids)
      .sort((a, b) => (b.winRate ?? -1) - (a.winRate ?? -1));

    const byClient = buildBreakdown(projects, p => p.ClientName)
      .filter(r => r.total >= minBids)
      .sort((a, b) => b.total - a.total)
      .slice(0, 12);

    const byType = buildBreakdown(projects, p => p.ProjectType)
      .sort((a, b) => b.total - a.total);

    const byState = buildBreakdown(projects, p => p.State)
      .filter(r => r.total >= minBids)
      .sort((a, b) => b.total - a.total);

    const byQuarter = buildBreakdown(projects, p => quarterKey(p))
      .sort((a, b) => a.key.localeCompare(b.key));

    return {
      total, awardedCount: awarded.length, notAwardedCount: notAwarded.length, decided, open,
      totalValue, awardedValue, notAwardedValue, decidedValue,
      winRate, hitRate, dollarWinRate, dollarHitRate,
      byEstimator, byClient, byType, byState, byQuarter,
    };
  }, [projects, minBids]);

  if (!canViewAnalytics(user)) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Not authorized</h3>
          <p>Analytics is available to executives and admins. Contact your admin if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading analytics...</div>;

  // Admin has enabled Advanced (Power BI). Users can still flip back to Standard.
  const advancedAvailable = reportingConfig.mode === 'advanced';
  const showAdvanced = activeMode === 'advanced' && advancedAvailable;

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2>Analytics</h2>
          {advancedAvailable && (
            <div className="reporting-mode-switch" title="Admin has enabled advanced (Power BI) reporting">
              <button
                className={`reporting-mode-btn ${activeMode === 'standard' ? 'active' : ''}`}
                onClick={() => setActiveMode('standard')}
              >
                Standard
              </button>
              <button
                className={`reporting-mode-btn ${activeMode === 'advanced' ? 'active' : ''}`}
                onClick={() => setActiveMode('advanced')}
              >
                Advanced
              </button>
            </div>
          )}
          {!showAdvanced && (
            <>
              <div className="scope-toggle">
                <button className={`scope-btn ${scope === 'mine' ? 'active' : ''}`} onClick={() => setScope('mine')}>
                  My Bids
                </button>
                <button className={`scope-btn ${scope === 'company' ? 'active' : ''}`} onClick={() => setScope('company')}>
                  Company
                </button>
              </div>
              {scope === 'mine' && user?.displayName && (
                <span className="scope-user">{user.displayName}</span>
              )}
            </>
          )}
        </div>
        {!showAdvanced && (
          <div className="analytics-controls">
            <label className="analytics-control-label">
              Min bids
              <select value={minBids} onChange={e => setMinBids(Number(e.target.value))}>
                <option value={1}>1</option>
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="page-content">
        {showAdvanced ? (
          <PowerBIReport config={reportingConfig} />
        ) : (
          <StandardAnalytics stats={stats} scope={scope} navigate={navigate} minBids={minBids} />
        )}
      </div>
    </>
  );
}

function StandardAnalytics({ stats, navigate, minBids }) {
  return (
    <>
        {/* Rate KPIs */}
        <div className="analytics-section">
          <h3 className="analytics-section-title">Bid Performance</h3>
          <div className="kpi-row analytics-kpi-row">
            <RateCard
              label="Win Rate"
              sublabel="Awarded / (Awarded + Not Awarded)"
              value={stats.winRate}
              numer={stats.awardedCount}
              denom={stats.decided}
              color="green"
            />
            <RateCard
              label="Hit Rate"
              sublabel="Awarded / All projects"
              value={stats.hitRate}
              numer={stats.awardedCount}
              denom={stats.total}
              color="purple"
            />
            <RateCard
              label="$ Win Rate"
              sublabel="$ Awarded / $ Decided"
              value={stats.dollarWinRate}
              numer={formatCurrency(stats.awardedValue)}
              denom={formatCurrency(stats.decidedValue)}
              color="blue"
            />
            <RateCard
              label="$ Hit Rate"
              sublabel="$ Awarded / $ All"
              value={stats.dollarHitRate}
              numer={formatCurrency(stats.awardedValue)}
              denom={formatCurrency(stats.totalValue)}
              color="cyan"
            />
          </div>
        </div>

        {/* Volume breakdown */}
        <div className="analytics-section">
          <div className="volume-row">
            <VolumeStat label="Total Projects" value={stats.total} />
            <VolumeStat label="Open" value={stats.open} />
            <VolumeStat label="Decided" value={stats.decided} />
            <VolumeStat label="Awarded" value={stats.awardedCount} tone="positive" />
            <VolumeStat label="Not Awarded" value={stats.notAwardedCount} tone="negative" />
          </div>
        </div>

        {/* Estimator + Quarter */}
        <div className="analytics-grid-2">
          <BreakdownTable
            title="Win Rate by Estimator"
            rows={stats.byEstimator}
            emptyText={`No estimators with ${minBids}+ bids`}
          />
          <QuarterChart rows={stats.byQuarter} />
        </div>

        {/* Client + Type */}
        <div className="analytics-grid-2">
          <BreakdownTable
            title="Win Rate by Client (Top 12)"
            rows={stats.byClient}
            emptyText={`No clients with ${minBids}+ bids`}
            onRowClick={(r) => navigate(`/clients/${encodeURIComponent(r.key)}`)}
          />
          <BreakdownTable
            title="Win Rate by Project Type"
            rows={stats.byType}
            emptyText="No project types"
          />
        </div>

        {/* State */}
        <div className="analytics-grid-1">
          <BreakdownTable
            title="Win Rate by State"
            rows={stats.byState}
            emptyText={`No states with ${minBids}+ bids`}
          />
        </div>
    </>
  );
}

function RateCard({ label, sublabel, value, numer, denom, color }) {
  return (
    <div className="kpi-card rate-card">
      <div className="rate-card-top">
        <div className={`rate-card-accent rate-card-${color}`} />
        <div className="rate-card-label">{label}</div>
      </div>
      <div className="rate-card-value">{value}%</div>
      <div className="rate-card-sublabel">{sublabel}</div>
      <div className="rate-card-fraction">
        <span className="rate-card-numer">{numer}</span>
        <span className="rate-card-slash"> / </span>
        <span className="rate-card-denom">{denom}</span>
      </div>
    </div>
  );
}

function VolumeStat({ label, value, tone }) {
  return (
    <div className={`volume-stat ${tone ? `volume-stat-${tone}` : ''}`}>
      <div className="volume-stat-value">{value}</div>
      <div className="volume-stat-label">{label}</div>
    </div>
  );
}

// Shared table for any group-by breakdown (estimator, client, type, state).
// Rows come in pre-sorted; this component just renders them with rate bars.
function BreakdownTable({ title, rows, emptyText, onRowClick }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="card dashboard-card">
        <div className="card-header"><h3>{title}</h3></div>
        <div className="card-body">
          <p className="empty-text">{emptyText}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="card dashboard-card">
      <div className="card-header"><h3>{title}</h3></div>
      <div className="card-body" style={{ padding: 0 }}>
        <table className="breakdown-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="num">Bids</th>
              <th className="num">W / L</th>
              <th className="num">Win %</th>
              <th className="num">Hit %</th>
              <th className="bar-col">Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr
                key={r.key}
                className={onRowClick ? 'breakdown-row-clickable' : ''}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
              >
                <td className="breakdown-name" title={r.key}>{r.key}</td>
                <td className="num">{r.total}</td>
                <td className="num">{r.awarded} / {r.notAwarded}</td>
                <td className="num">{r.winRate != null ? `${r.winRate}%` : '—'}</td>
                <td className="num">{r.hitRate != null ? `${r.hitRate}%` : '—'}</td>
                <td className="bar-col">
                  <div className="rate-bars">
                    <div className="rate-bar-track">
                      <div
                        className="rate-bar-fill rate-bar-win"
                        style={{ width: `${r.winRate ?? 0}%` }}
                        title={`Win rate ${r.winRate ?? 0}%`}
                      />
                    </div>
                    <div className="rate-bar-track">
                      <div
                        className="rate-bar-fill rate-bar-hit"
                        style={{ width: `${r.hitRate ?? 0}%` }}
                        title={`Hit rate ${r.hitRate ?? 0}%`}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QuarterChart({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="card dashboard-card">
        <div className="card-header"><h3>Win Rate by Quarter</h3></div>
        <div className="card-body"><p className="empty-text">No dated bids yet</p></div>
      </div>
    );
  }
  const maxTotal = Math.max(1, ...rows.map(r => r.total));
  return (
    <div className="card dashboard-card">
      <div className="card-header">
        <h3>Win Rate by Quarter</h3>
        <span className="badge-count">bucketed by submit date</span>
      </div>
      <div className="card-body">
        <div className="quarter-legend">
          <span><span className="swatch swatch-awarded" /> Awarded</span>
          <span><span className="swatch swatch-not-awarded" /> Not Awarded</span>
          <span><span className="swatch swatch-open" /> Open</span>
        </div>
        <div className="quarter-chart">
          {rows.map(r => {
            const open = r.total - r.awarded - r.notAwarded;
            const heightPct = (r.total / maxTotal) * 100;
            return (
              <div key={r.key} className="quarter-col" title={`${r.key}: ${r.total} bids`}>
                <div className="quarter-bar-wrapper">
                  <div className="quarter-bar" style={{ height: `${heightPct}%` }}>
                    {r.awarded > 0 && (
                      <div className="quarter-seg quarter-seg-awarded" style={{ flex: r.awarded }} title={`Awarded: ${r.awarded}`} />
                    )}
                    {r.notAwarded > 0 && (
                      <div className="quarter-seg quarter-seg-not-awarded" style={{ flex: r.notAwarded }} title={`Not Awarded: ${r.notAwarded}`} />
                    )}
                    {open > 0 && (
                      <div className="quarter-seg quarter-seg-open" style={{ flex: open }} title={`Open: ${open}`} />
                    )}
                  </div>
                </div>
                <div className="quarter-winrate">{r.winRate != null ? `${r.winRate}%` : '—'}</div>
                <div className="quarter-label">{r.key}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
